/**
 * Filtros avanzados de la lista de entradas (los del panel lateral).
 *
 * Viven en un solo objeto, `advanced`, para que el panel los edite como
 * borrador y ListaEntrada los reciba de una vez: un cambio = una consulta.
 * La forma del objeto y los nombres de los params coinciden con
 * `EntradaDetalleFilterDTO` del backend.
 *
 * Sin dependencias de Vue: se prueba aparte (tests/unit/views/entradaFilters.spec.js).
 */

export const STATUS_VIEWS = [
    { value: 'active', label: 'Activas' },
    { value: 'cancelled', label: 'Canceladas' },
    { value: 'all', label: 'Todas' }
];

// `key` es el prefijo del param (mermaMin, existenciaMax) y de la clave en `advanced.ranges`.
// Solo estos dos: responden preguntas reales (¿qué lotes tuvieron merma?, ¿cuáles se están
// acabando?). Ingreso, traspaso y ventas se resuelven ordenando la columna.
export const RANGE_FIELDS = [
    { key: 'merma', label: 'Mermas' },
    { key: 'existencia', label: 'Existencia' }
];

export const emptyAdvanced = () => ({
    productId: null,
    sucursalId: null,
    typeEntryIds: [],
    status: 'active',
    ranges: RANGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: { min: '', max: '' } }), {})
});

// Copia profunda: el panel edita un borrador, no el objeto aplicado.
export const cloneAdvanced = (advanced) => ({
    ...advanced,
    typeEntryIds: [...advanced.typeEntryIds],
    ranges: RANGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: { ...advanced.ranges[key] } }), {})
});

const hasValue = (value) => value !== '' && value !== null && value !== undefined;

export const hasRange = (range) => !!range && (hasValue(range.min) || hasValue(range.max));

// Mínimo mayor que el máximo. El backend también lo rechaza, pero aquí se avisa antes de enviar.
export const isRangeInverted = (range) =>
    !!range && hasValue(range.min) && hasValue(range.max) && Number(range.min) > Number(range.max);

export const hasInvertedRange = (advanced) => RANGE_FIELDS.some(({ key }) => isRangeInverted(advanced.ranges[key]));

/**
 * Params que se mezclan en `tableFilters`. Lo vacío va como undefined: axios no
 * lo manda y el backend usa sus defaults. La lista de tipos va separada por
 * comas porque axios serializaría el arreglo como `typeEntryId[]=1`.
 */
export const advancedParams = (advanced) => {
    const params = {
        productId: advanced.productId || undefined,
        sucursalId: advanced.sucursalId || undefined,
        typeEntryId: advanced.typeEntryIds.length ? advanced.typeEntryIds.join(',') : undefined,
        status: advanced.status !== 'active' ? advanced.status : undefined
    };
    RANGE_FIELDS.forEach(({ key }) => {
        const { min, max } = advanced.ranges[key];
        params[`${key}Min`] = hasValue(min) ? min : undefined;
        params[`${key}Max`] = hasValue(max) ? max : undefined;
    });
    return params;
};

const rangeLabel = (label, { min, max }) => {
    if (hasValue(min) && hasValue(max)) return `${label}: ${min} – ${max}`;
    if (hasValue(min)) return `${label}: ≥ ${min}`;
    return `${label}: ≤ ${max}`;
};

/**
 * Chips de lo aplicado, con la `key` que `removeAdvanced` entiende.
 * `catalogs`: { products, sucursales, tipoEntrada } para poner nombres, no ids.
 */
export const advancedChips = (advanced, catalogs = {}) => {
    const nameOf = (list = [], id, field = 'nombre') => {
        const found = list.find((item) => item.id === id);
        return found ? found[field] : id;
    };
    const chips = [];
    if (advanced.productId) {
        chips.push({ key: 'product', icon: 'fas fa-drumstick-bite', label: `Producto: ${nameOf(catalogs.products, advanced.productId)}` });
    }
    if (advanced.sucursalId) {
        chips.push({ key: 'sucursal', icon: 'fas fa-store-alt', label: `Sucursal: ${nameOf(catalogs.sucursales, advanced.sucursalId)}` });
    }
    if (advanced.typeEntryIds.length) {
        const names = advanced.typeEntryIds.map((id) => nameOf(catalogs.tipoEntrada, id)).join(', ');
        chips.push({ key: 'types', icon: 'fas fa-tags', label: `Tipo: ${names}` });
    }
    if (advanced.status !== 'active') {
        const view = STATUS_VIEWS.find((v) => v.value === advanced.status);
        chips.push({ key: 'status', icon: 'fas fa-toggle-on', label: `Estado: ${view ? view.label : advanced.status}` });
    }
    RANGE_FIELDS.forEach(({ key, label }) => {
        if (hasRange(advanced.ranges[key])) {
            chips.push({ key: `range:${key}`, icon: 'fas fa-sliders-h', label: rangeLabel(label, advanced.ranges[key]) });
        }
    });
    return chips;
};

// Cuántos filtros del panel hay activos (el contador del icono). "Activas" es el valor por defecto, no cuenta.
export const countAdvanced = (advanced) => advancedChips(advanced).length;

// Devuelve un `advanced` nuevo sin el filtro del chip `key`.
export const removeAdvanced = (advanced, key) => {
    const next = cloneAdvanced(advanced);
    if (key === 'product') next.productId = null;
    if (key === 'sucursal') next.sucursalId = null;
    if (key === 'types') next.typeEntryIds = [];
    if (key === 'status') next.status = 'active';
    if (key.startsWith('range:')) next.ranges[key.slice('range:'.length)] = { min: '', max: '' };
    return next;
};
